import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
    Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    TablePagination, Checkbox, Box, Typography
} from '@mui/material';
import { FilterList} from '@mui/icons-material';
import {
    deleteCatalog
} from '../services/projectService';
import '../App.css';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';
import {Catalog} from "../interfaces/Catalog";

interface CatalogTableProps {
    catalogs: Catalog[];
    onEditCatalogs: (project: Catalog) => void;
    refreshCatalogs: () => void;
}

const CatalogTable: React.FC<CatalogTableProps> = ({ catalogs, onEditCatalogs, refreshCatalogs }) => {
    const [filter, setFilter] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [catalogToDelete, setCatalogToDelete] = useState<Catalog | null>(null);
    const [page, setPage] = useState(0); // Page actuelle
    const [rowsPerPage, setRowsPerPage] = useState(10); // Nombre de lignes par page
    const [selectedCatalogs, setSelectedCatalogs] = useState<number[]>([]); // Projets sélectionnés

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setCatalogToDelete(null);
    };

    const confirmDelete = async () => {
        if (catalogToDelete) {
            try {
                await deleteCatalog(catalogToDelete.id);
                refreshCatalogs();
            } catch (error) {
                console.error('Erreur lors de la suppression du catalogue:', error);
            } finally {
                closeDeleteModal();
            }
        }
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedCatalogs(filteredCatalogs.map(catalog => catalog.id));
        } else {
            setSelectedCatalogs([]);
        }
    };

    const handleSelectCatalog = (catalogId: number) => {
        setSelectedCatalogs(prevSelected =>
            prevSelected.includes(catalogId)
                ? prevSelected.filter(id => id !== catalogId)
                : [...prevSelected, catalogId]
        );
    };

    const filteredCatalogs = catalogs
        .filter(catalog =>
            catalog.libelle.toLowerCase().includes(filter.toLowerCase())
        );

    // Pagination logic
    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedProjects = filteredCatalogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper elevation={3} sx={{ p: 3, width: '100%', position: 'relative' }}>

            {/* Panneau de filtres */}
            <Box mb={3} p={2} sx={{ border: '2px solid #4caf50', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    <FilterList sx={{ mr: 1, color: '#4caf50' }} />
                    Filtres
                </Typography>

                <Box sx={{ columns: 3, mt: 2 }}>
                    <TextField
                        label="Libellé du catalogue"
                        variant="outlined"
                        fullWidth
                        style={{ marginTop: 2 }}
                        margin="normal"
                        value={filter}
                        onChange={handleFilterChange}
                    />


                </Box>
            </Box>

            {filteredCatalogs.length > 0 ? (
                <Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                    >
                        {selectedCatalogs.length > 0 && (
                            <>


                            </>

                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                position: 'relative',
                                top: 8,
                                mb: 3,
                            }}
                            onClick={() => {
                                refreshCatalogs()
                            }}
                        >
                            Rechercher
                        </Button>
                    </Box>

                    <TableContainer component={Paper} sx={{ border: '2px solid #2196f3' }}>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#2196f3' }}>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedCatalogs.length === filteredCatalogs.length}
                                            indeterminate={selectedCatalogs.length > 0 && selectedCatalogs.length < filteredCatalogs.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>ID</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>Libellé</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>Debut</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>Fin</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color:'white' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedProjects.map((catalog) => (
                                    <TableRow
                                        key={catalog.id}
                                        sx={{
                                            backgroundColor: selectedCatalogs.includes(catalog.id) ? '#e3f2fd' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: selectedCatalogs.includes(catalog.id) ? '#e3f2fd' : '#f5f5f5', // Couleur de surbrillance
                                                cursor: 'pointer'
                                            }
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedCatalogs.includes(catalog.id)}
                                                onChange={() => handleSelectCatalog(catalog.id)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">{catalog.id}</TableCell>
                                        <TableCell align="center">{catalog.libelle}</TableCell>
                                        <TableCell align="center">{formatDateToDDMMYYYY(catalog.debut)}</TableCell>
                                        <TableCell align="center">{formatDateToDDMMYYYY(catalog.fin)}</TableCell>
                                        <TableCell align="center">
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={filteredCatalogs.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            ) : (
                <p>Aucun catalogue trouvé.</p>
            )}

            {/* Modal de confirmation de suppression */}
            <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer le catalogue "{catalogToDelete?.libelle}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteModal} color="primary">
                        Annuler
                    </Button>
                    <Button onClick={confirmDelete} color="error">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default CatalogTable;